/**
 * TasksPanel -- full CRUD task management panel for the command center.
 *
 * Features:
 * - Inline "Add task" form at top
 * - Task list with checkbox to complete, click to expand, delete button
 * - Inline editing of title
 * - Filter by status (active / done / all)
 * - Group by project (flat list or grouped by project)
 */

import { useState } from "react";
import type { SidebarTask } from "./types";

interface TasksPanelProps {
  tasks: SidebarTask[];
  projects?: Array<{ id: string; name: string }>;
  onTaskClick: (task: SidebarTask) => void;
  onCreateTask: (input: { title: string; priority?: string; dueDate?: string; projectId?: string; consequenceOfDelay?: string }) => void;
  onUpdateTask: (id: string, updates: Record<string, unknown>) => void;
  onDeleteTask: (id: string) => void;
}

type FilterMode = "active" | "done" | "all";
type GroupMode = "none" | "project";

const PRIORITY_COLORS: Record<string, string> = {
  critical: "text-error",
  high: "text-warning",
  medium: "text-text-secondary",
  low: "text-text-tertiary",
};

function sortTasks(tasks: SidebarTask[]): SidebarTask[] {
  return [...tasks].sort((a, b) => {
    const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
    const aPri = priorityOrder[a.priority ?? "medium"] ?? 2;
    const bPri = priorityOrder[b.priority ?? "medium"] ?? 2;
    if (aPri !== bPri) return aPri - bPri;
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });
}

export function TasksPanel({
  tasks,
  projects: profileProjects,
  onTaskClick,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
}: TasksPanelProps): React.ReactElement {
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState<string>("medium");
  const [newDueDate, setNewDueDate] = useState("");
  const [newProject, setNewProject] = useState("");
  const [newConsequence, setNewConsequence] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [filter, setFilter] = useState<FilterMode>("active");
  const [groupBy, setGroupBy] = useState<GroupMode>("none");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Collect known projects: merge profile projects with task-derived projects, dedupe by name
  const projects = (() => {
    const profileNames = (profileProjects ?? []).map(p => p.name);
    const taskNames = tasks.map(t => t.project).filter(Boolean) as string[];
    return Array.from(new Set([...profileNames, ...taskNames])).sort();
  })();

  const handleAddTask = () => {
    if (!newTitle.trim()) return;
    onCreateTask({
      title: newTitle.trim(),
      priority: newPriority || undefined,
      dueDate: newDueDate || undefined,
      projectId: newProject || undefined,
      consequenceOfDelay: newConsequence.trim() || undefined,
    });
    setNewTitle("");
    setNewPriority("medium");
    setNewDueDate("");
    setNewProject("");
    setNewConsequence("");
    setShowAddForm(false);
  };

  const handleToggleComplete = (task: SidebarTask) => {
    const newStatus = task.status === "done" ? "inbox" : "done";
    onUpdateTask(task.id, { status: newStatus });
  };

  const startEditTitle = (task: SidebarTask) => {
    setEditingId(task.id);
    setEditingTitle(task.title);
  };

  const saveEditTitle = (taskId: string) => {
    if (editingTitle.trim() && editingTitle.trim() !== tasks.find(t => t.id === taskId)?.title) {
      onUpdateTask(taskId, { title: editingTitle.trim() });
    }
    setEditingId(null);
    setEditingTitle("");
  };

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // Filter tasks
  const filteredTasks = tasks.filter((task) => {
    if (filter === "active") return task.status !== "done" && task.status !== "archived";
    if (filter === "done") return task.status === "done";
    return task.status !== "archived";
  });

  const activeCount = tasks.filter(t => t.status !== "done" && t.status !== "archived").length;
  const doneCount = tasks.filter(t => t.status === "done").length;

  // Build grouped or flat list
  const groups: { key: string; label: string; tasks: SidebarTask[] }[] = [];

  if (groupBy === "project") {
    const byProject = new Map<string, SidebarTask[]>();
    for (const task of filteredTasks) {
      const key = task.project || "";
      if (!byProject.has(key)) byProject.set(key, []);
      byProject.get(key)!.push(task);
    }
    // Named projects first (sorted), then "No project" last
    const sortedKeys = Array.from(byProject.keys()).sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
    for (const key of sortedKeys) {
      groups.push({
        key,
        label: key || "No project",
        tasks: sortTasks(byProject.get(key)!),
      });
    }
  } else {
    groups.push({ key: "__all__", label: "", tasks: sortTasks(filteredTasks) });
  }

  const renderTask = (task: SidebarTask) => {
    const isDone = task.status === "done";
    const isEditing = editingId === task.id;

    return (
      <div
        key={task.id}
        className="group flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-surface-hover transition-colors duration-100"
      >
        {/* Checkbox */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleComplete(task);
          }}
          className={[
            "flex-shrink-0 w-5 h-5 mt-0.5 rounded border flex items-center justify-center transition-colors",
            isDone
              ? "bg-primary border-primary"
              : "border-text-tertiary hover:border-primary hover:bg-primary/10",
          ].join(" ")}
        >
          {isDone && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        {/* Task content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              onBlur={() => saveEditTitle(task.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEditTitle(task.id);
                if (e.key === "Escape") { setEditingId(null); setEditingTitle(""); }
              }}
              className="w-full text-sm text-text-primary bg-surface border border-border rounded px-2 py-0.5 focus:outline-none focus:border-primary"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={() => onTaskClick(task)}
              onDoubleClick={() => startEditTitle(task)}
              className={[
                "text-sm text-left truncate w-full",
                isDone ? "text-text-tertiary line-through" : "text-text-primary",
              ].join(" ")}
            >
              {task.title}
            </button>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {task.priority && task.priority !== "medium" && (
              <span className={`text-xs font-mono ${PRIORITY_COLORS[task.priority] ?? ""}`}>
                {task.priority}
              </span>
            )}
            {task.dueDate && (
              <span className={[
                "text-xs font-mono",
                !isDone && task.dueDate < new Date().toISOString().split("T")[0]
                  ? "text-error"
                  : "text-text-tertiary",
              ].join(" ")}>
                {task.dueDate}
              </span>
            )}
            {/* Show project label only when not grouped by project */}
            {groupBy !== "project" && task.project && (
              <span className="text-xs font-mono text-text-tertiary truncate">
                {task.project}
              </span>
            )}
            {task.reschedulingCount != null && task.reschedulingCount >= 2 && (
              <span className="text-xs font-mono text-warning" title={`Rescheduled ${task.reschedulingCount} times`}>
                {task.reschedulingCount >= 3 ? "\u26A0" : "\u21BB"}{task.reschedulingCount}x
              </span>
            )}
            {task.aiPriority && task.aiPriority !== task.priority && (
              <span className="text-xs font-mono text-primary" title="AI-suggested priority">
                AI:{task.aiPriority}
              </span>
            )}
          </div>
        </div>

        {/* Delete button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteTask(task.id);
          }}
          className="flex-shrink-0 p-1 rounded hover:bg-surface text-text-tertiary hover:text-error transition-colors opacity-0 group-hover:opacity-100"
          title="Delete task"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Header: filters + group toggle + add */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {(["active", "done", "all"] as FilterMode[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={[
                "text-xs px-2 py-1 rounded transition-colors",
                filter === f
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-text-tertiary hover:text-text-secondary",
              ].join(" ")}
            >
              {f === "active" ? `Active (${activeCount})` : f === "done" ? `Done (${doneCount})` : "All"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {/* Group by project toggle */}
          <button
            type="button"
            onClick={() => setGroupBy(prev => prev === "none" ? "project" : "none")}
            className={[
              "text-xs px-2 py-1 rounded transition-colors",
              groupBy === "project"
                ? "bg-primary/10 text-primary font-medium"
                : "text-text-tertiary hover:text-text-secondary",
            ].join(" ")}
            title="Group by project"
          >
            <svg className="w-3.5 h-3.5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
            Projects
          </button>
          {/* Add button */}
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add
          </button>
        </div>
      </div>

      {/* Add task form */}
      {showAddForm && (
        <div className="bg-surface border border-border rounded-lg p-3 space-y-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddTask();
              if (e.key === "Escape") setShowAddForm(false);
            }}
            placeholder="Task title..."
            className="w-full text-sm text-text-primary bg-transparent border-none outline-none placeholder:text-text-tertiary"
            autoFocus
          />
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(e.target.value)}
              className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary"
            />
            {/* Project selector */}
            <select
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary"
            >
              <option value="">No project</option>
              {projects.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <input
              type="text"
              value={newConsequence}
              onChange={(e) => setNewConsequence(e.target.value)}
              placeholder="What happens if delayed?"
              className="text-xs bg-surface border border-border rounded px-2 py-1 text-text-secondary flex-1"
            />
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="text-xs text-text-tertiary hover:text-text-secondary px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAddTask}
              disabled={!newTitle.trim()}
              className="text-xs bg-primary text-white rounded px-3 py-1 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Task list */}
      {filteredTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <svg className="w-5 h-5 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-text-tertiary text-sm">
            {filter === "done" ? "No completed tasks" : "No tasks yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {groups.map((group) => (
            <div key={group.key}>
              {/* Group header (only when grouping by project) */}
              {groupBy === "project" && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider hover:bg-surface-hover rounded transition-colors"
                >
                  <svg
                    className={`w-3 h-3 transition-transform ${collapsedGroups.has(group.key) ? "" : "rotate-90"}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  {group.label}
                  <span className="text-text-tertiary font-normal ml-1">({group.tasks.length})</span>
                </button>
              )}
              {/* Tasks in this group */}
              {!collapsedGroups.has(group.key) && (
                <div className="space-y-0.5">
                  {group.tasks.map(renderTask)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
